import { Easing, Tween } from "@tweenjs/tween.js";
import { match, P } from "ts-pattern";
import { SpriteFadeInEffect } from "@/editor/blueprints/SpriteFadeInEffect";
import { SpriteFadeOutEffect } from "@/editor/blueprints/SpriteFadeOutEffect";
import { SpriteEffectConfig } from "@/client/communication/api/bindings/SpriteEffectConfig";

export type SpriteEffectProperties = {
  pos_x: number;
  pos_y: number;
  scale_x: number;
  scale_y: number;
  rotation: number;
  alpha: number;
  color: ColorMatrixProps;
};
export type ColorMatrixProps = {
  brightness: number;
  tint_intensity: number;
  tint_color: string;
  hue: number;
};
export type SpriteEffect = {
  add_props: SpriteEffectProperties;
  tween: Tween<SpriteEffectProperties>;
  all_tweens: Tween<SpriteEffectProperties>[];
};

function create_base_props(
  props?: Partial<SpriteEffectProperties>,
): SpriteEffectProperties {
  return {
    pos_x: props?.pos_x ?? 0,
    pos_y: props?.pos_y ?? 0,
    scale_x: props?.scale_x ?? 0,
    scale_y: props?.scale_y ?? 0,
    rotation: props?.rotation ?? 0,
    alpha: props?.alpha ?? 0,
    color: {
      tint_intensity: 0,
      tint_color: "#ffffff",
      brightness: 0,
      hue: 0,
    },
  };
}

export function create_no_effect(
  _duration: number,
  _delay: number,
): SpriteEffect {
  const add_props = create_base_props();
  const tween = new Tween(add_props).to({ scale_x: 0.0 }, 0).delay(0);
  return {
    add_props,
    tween: tween,
    all_tweens: [tween],
  };
}

export function create_sprite_effect_from_config(
  effect_config: SpriteEffectConfig,
): SpriteEffect {
  return match(effect_config)
    .with(
      { ColorEffect: P.select() },
      ([
        effect_props,
        easing_1,
        easing_type_1,
        duration_1,
        easing_2,
        easing_type_2,
        duration_2,
      ]) => {
        const sprite_effect_properties: Partial<SpriteEffectProperties> = {
          color: match(effect_props)
            .with({ Hue: P.select() }, (hue) => {
              return {
                hue,
              } as ColorMatrixProps;
            })
            .with({ Brightness: P.select() }, (brightness) => {
              return {
                brightness,
              } as ColorMatrixProps;
            })
            .with({ TintIntensity: P.select() }, (tint_intensity) => {
              return {
                tint_intensity,
              } as ColorMatrixProps;
            })
            .exhaustive(),
        };
        return create_two_step_animation(
          sprite_effect_properties,
          duration_1,
          duration_2,
          Easing[easing_1][easing_type_1],
          Easing[easing_2][easing_type_2],
        );
      },
    )
    .exhaustive();
}

export function create_two_step_animation(
  target: Partial<SpriteEffectProperties>,
  duration_1: number,
  duration_2: number,
  easing_1: typeof Easing.Cubic.In = Easing.Linear.None,
  easing_2: typeof Easing.Cubic.In = Easing.Linear.None,
  delay: number = 0,
): SpriteEffect {
  const add_props = create_base_props();
  const reverse_target = { ...target, color: { ...target.color } };
  for (const key of Object.keys(target) as (keyof SpriteEffectProperties)[]) {
    if (typeof reverse_target[key] === "number") {
      reverse_target[key] = -reverse_target[key]!;
    }
    if (reverse_target.color) {
      for (const color_key of Object.keys(
        reverse_target.color,
      ) as (keyof SpriteEffectProperties["color"])[]) {
        const prop_value = reverse_target.color[color_key];
        if (prop_value !== undefined && typeof prop_value === "number") {
          (reverse_target.color as unknown as { [key: string]: number })[
            color_key
          ] = 0;
        }
      }
    }
  }
  const tween_1 = new Tween(add_props)
    .easing(easing_1)
    .to(target, duration_1)
    .delay(delay);
  const tween_2 = new Tween(add_props)
    .easing(easing_2)
    .to(reverse_target, duration_2)
    .delay(delay);
  return {
    add_props,
    tween: tween_1.chain(tween_2),
    all_tweens: [tween_1, tween_2],
  };
}

export function create_simple_fade_in(
  duration: number,
  delay: number,
): SpriteEffect {
  const add_props = create_base_props({
    alpha: -1,
  });
  const tween = new Tween(add_props)
    .easing(Easing.Cubic.In)
    .to({ alpha: 0.0 }, duration)
    .delay(delay);
  return {
    add_props,
    tween: tween,
    all_tweens: [tween],
  };
}

export function create_simple_fade_out(
  duration: number,
  delay: number,
): SpriteEffect {
  const add_props = create_base_props();
  const tween = new Tween(add_props)
    .easing(Easing.Cubic.In)
    .to({ alpha: -2 }, duration)
    .delay(delay);
  return {
    add_props,
    tween: tween,
    all_tweens: [tween],
  };
}

export function create_tile_fade_in_animation(
  duration: number,
  delay: number,
): SpriteEffect {
  const add_props = create_base_props({
    scale_x: -1,
    scale_y: -1,
  });
  const tween_1 = new Tween(add_props)
    .to({ scale_x: 1.0, scale_y: 1.0 }, duration * (1 / 2))
    .easing(Easing.Quadratic.Out)
    .delay(delay);
  const tween_2 = new Tween(add_props)
    .to({ scale_x: 0, scale_y: 0 }, duration * (1 / 2))
    .easing(Easing.Quadratic.In);
  return {
    add_props,
    tween: tween_1.chain(tween_2),
    all_tweens: [tween_1, tween_2],
  };
}

export function create_fade_in_animation(
  fade_in_effect: SpriteFadeInEffect,
  duration: number,
): SpriteEffect {
  return match(fade_in_effect)
    .with("None", () => create_no_effect(duration, 0))
    .with("Fade", () => create_simple_fade_in(duration, 0))
    .with("Inflate", () => create_inflate_animation(duration, 0))
    .with("JumpForth", () => create_jump_forth_animation(duration, 0))
    .exhaustive();
}

export function create_fade_out_animation(
  fade_out_effect: SpriteFadeOutEffect,
  duration: number,
): SpriteEffect {
  return match(fade_out_effect)
    .with("None", () => create_no_effect(duration, 0))
    .with("Fade", () => create_simple_fade_out(duration, 0))
    .with("Deflate", () => create_deflate_animation(duration, 0))
    .with("JumpBack", () => create_jump_back_animation(duration, 0))
    .exhaustive();
}

export function create_jump_back_animation(
  duration: number,
  delay: number,
): SpriteEffect {
  const add_props = create_base_props();
  const tween_1 = new Tween(add_props)
    .to(
      { pos_y: -80, alpha: -0.5, scale_x: -0.5, scale_y: -0.5 },
      duration * (1 / 2),
    )
    .easing(Easing.Quadratic.In)
    .delay(delay);
  const tween_2 = new Tween(add_props)
    .to(
      { pos_y: -30, scale_x: -0.8, scale_y: -0.8, alpha: -1 },
      duration * (1 / 2),
    )
    .easing(Easing.Quadratic.Out);
  return {
    add_props,
    tween: tween_1.chain(tween_2),
    all_tweens: [tween_1, tween_2],
  };
}

export function create_deflate_animation(
  duration: number,
  delay: number,
): SpriteEffect {
  const add_props = create_base_props();
  const tween_1 = new Tween(add_props)
    .to({ scale_x: 0.25, scale_y: 0.25 }, duration / 2)
    .easing(Easing.Cubic.In)
    .delay(delay);

  const tween_2 = new Tween(add_props)
    .to({ scale_x: -0.8, scale_y: -0.8, alpha: -2 }, duration / 2)
    .easing(Easing.Cubic.Out)
    .delay(delay);
  return {
    add_props,
    tween: tween_1.chain(tween_2),
    all_tweens: [tween_1, tween_2],
  };
}

export function create_jump_forth_animation(
  duration: number,
  delay: number,
): SpriteEffect {
  const add_props = create_base_props();
  const tween_1 = new Tween(add_props)
    .to(
      { pos_y: -80, alpha: -0.5, scale_x: -0.5, scale_y: -0.5 },
      duration * (1 / 2),
    )
    .easing(Easing.Quadratic.Out)
    .delay(delay);
  const tween_2 = new Tween(add_props)
    .to({ pos_y: 0, scale_x: 0, scale_y: 0, alpha: 0 }, duration * (1 / 2))
    .easing(Easing.Quadratic.In);
  return {
    add_props,
    tween: tween_1.chain(tween_2),
    all_tweens: [tween_1, tween_2],
  };
}

export function create_inflate_animation(
  duration: number,
  delay: number,
): SpriteEffect {
  const add_props = create_base_props({
    scale_x: -0.8,
    scale_y: -0.8,
    alpha: -1,
  });
  const tween_1 = new Tween(add_props)
    .to({ alpha: 1, scale_x: 0.25, scale_y: 0.25 }, duration / 2)
    .easing(Easing.Cubic.Out)
    .delay(delay);

  const tween_2 = new Tween(add_props)
    .to({ scale_x: 0, scale_y: 0 }, duration / 2)
    .easing(Easing.Cubic.In)
    .delay(delay);
  return {
    add_props,
    tween: tween_1.chain(tween_2),
    all_tweens: [tween_1, tween_2],
  };
}

export function create_tile_fade_out_animation(
  duration: number,
  delay: number,
): SpriteEffect {
  const add_props = create_base_props();
  const tween_1 = new Tween(add_props)
    .to({ scale_x: 0.3, scale_y: 0.3 }, duration * (1 / 2))
    .easing(Easing.Quadratic.In)
    .delay(delay);
  const tween_2 = new Tween(add_props)
    .to({ scale_x: -1, scale_y: -1 }, duration * (1 / 2))
    .easing(Easing.Quadratic.Out);
  return {
    add_props,
    tween: tween_1.chain(tween_2),
    all_tweens: [tween_1, tween_2],
  };
}
